import { useCallback, useMemo, useState } from 'react';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { errorResult, runJob } from '@/lib/http/runJob';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { Result } from '@/lib/http/HttpClient';
import { transformToolsData } from '../utils/transformToolsData';
import type { ReferencePreview } from '../utils/transformToolsData';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';

export function useToolsFetch(): {
  isLoading: boolean;
  apiResult: Result<AllToolsResponse> | null;
  hierarchy: ReferencePreview[];
  handleRetry: () => void;
} {
  const [apiResult, setApiResult] = useState<Result<AllToolsResponse> | null>(null);
  const [hierarchy, setHierarchy] = useState<ReferencePreview[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const http = useApiClient();

  const traceabilityApiClient = useMemo<TraceabilityApiClient | null>(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const fetchTools = useCallback(
    async (signal: AbortSignal) => {
      if (!traceabilityApiClient) {
        return errorResult('TraceabilityApiClient not initialized', 'InitializationError');
      }

      return runJob<AllToolsResponse>({
        initFn: () => traceabilityApiClient.toolReadAll(undefined, { signal }),
        statusFn: (jobId) => traceabilityApiClient.toolsStatus({ jobId }, { signal }),
        signal,
        fallbackMessage: 'Failed to load tools',
      });
    },
    [traceabilityApiClient]
  );

  const { isLoading } = useApiCall<AllToolsResponse>({
    apiFn: fetchTools,
    deps: [refreshTick],
    immediate: !!traceabilityApiClient,
    onSuccess: (data) => {
      setApiResult({ ok: true, data } as Result<AllToolsResponse>);
      if ('tools' in data && Array.isArray(data.tools)) {
        const transformedData = transformToolsData(data);
        setHierarchy(transformedData);
      } else {
        setHierarchy([]);
      }
    },
    onError: () => {
      setApiResult(null);
      setHierarchy([]);
    },
  });

  const handleRetry = useCallback(() => {
    setRefreshTick((value) => value + 1);
  }, []);

  return { isLoading, apiResult, hierarchy, handleRetry };
}
